import { cva, type VariantProps } from 'class-variance-authority';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2.5 rounded-md border-2 font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      variant: {
        primary: 'border-navy bg-navy text-cream hover:bg-navy-deep hover:border-navy-deep',
        teal: 'border-teal bg-teal text-navy-deep hover:bg-teal-light hover:border-teal-light',
        outline: 'border-navy bg-transparent text-navy hover:bg-navy hover:text-cream',
        ghost: 'border-transparent bg-transparent text-navy hover:bg-sand',
        danger: 'border-danger bg-danger text-white hover:bg-[#8f2a2a] hover:border-[#8f2a2a]'
      },
      size: {
        sm: 'min-h-11 px-4 text-sm',
        md: 'min-h-12 px-6 text-base',
        lg: 'min-h-14 px-8 text-lg',
        icon: 'size-12 p-0'
      },
      block: {
        true: 'w-full',
        false: ''
      }
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false }
  }
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  loadingText?: string;
  children?: ReactNode;
}

export const Button = ({
  className,
  variant,
  size,
  block,
  loading = false,
  loadingText,
  disabled,
  children,
  type = 'button',
  ...props
}: ButtonProps) => (
  <button
    type={type}
    className={cn(buttonVariants({ variant, size, block }), className)}
    disabled={disabled || loading}
    aria-busy={loading}
    {...props}
  >
    {loading && <Loader2 className="size-5 animate-spin" aria-hidden="true" />}
    {loading && loadingText ? loadingText : children}
  </button>
);
